import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WebsiteMessageService, WebsiteMessage } from '../../services/websiteMessageService';
import { MessageCacheService } from '../../services/messageCache';
import { useAuth } from '../../context/AuthContext';
import './WebsiteMessage.css';
import { collapseTextChangeRangesAcrossMultipleVersions } from 'typescript';

// Define a type assertion function to check if a string is a valid message type
const isValidMessageType = (type: string): type is 'info' | 'welcome' | 'announcement' | 'maintenance' => {
  return ['info', 'welcome', 'announcement', 'maintenance'].includes(type);
};

const WebsiteMessageComponent: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState<WebsiteMessage[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showAllMessages, setShowAllMessages] = useState(false);

  useEffect(() => {
    // Only load messages if user is authenticated
    if (!user) {
      setLoading(false);
      setIsVisible(false);
      return;
    }

    // Initial load of messages
    loadMessages();
    
    // Set up real-time subscription for new messages
    const unsubscribe = WebsiteMessageService.subscribeToMessages((newMessage) => {
      console.log('Received real-time message update:', newMessage);
      
      // Check if this message has been dismissed by this specific user
      const dismissedIds = WebsiteMessageService.getDismissedMessageIds(user.id);
      if (dismissedIds.includes(newMessage.id)) {
        return; // Skip this message if it's been dismissed by this user
      }
      
      // Check if the message is active and within date range, and not a user-specific message
      // User-specific messages (task, team, survey, project) should only appear in the messages page
      const now = new Date().toISOString();
      if (
        newMessage.is_active && 
        newMessage.start_date <= now && 
        (!newMessage.end_date || newMessage.end_date >= now) &&
        !['task_pending_review', 'task_approved', 'task_rejected', 'team_application_received', 'team_application_approved', 'team_application_rejected', 'team_formed', 'survey_completed', 'project_release'].includes(newMessage.message_type)
      ) {
        // Add the new message to the list if it doesn't exist yet
        setMessages(prevMessages => {
          // Check if message already exists
          const exists = prevMessages.some(msg => msg.id === newMessage.id);
          
          if (!exists) {
            // Add new message to the beginning of the list
            const updatedMessages = [newMessage, ...prevMessages];
            
            // If this is the first message, make the component visible
            if (prevMessages.length === 0) {
              setIsVisible(true);
            }
            
            return updatedMessages;
          }
          
          // If message exists, update it
          return prevMessages.map(msg => 
            msg.id === newMessage.id ? newMessage : msg
          );
        });
      }
    });
    
    // Listen for localStorage changes to sync with messages page
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key && event.key.startsWith(`dismissed_message_${user.id}_`)) {
        // A message was dismissed by this user, reload messages to update display
        loadMessages();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events from the same page (localStorage events don't fire on same page)
    const handleMessageDismissed = () => {
      loadMessages();
    };
    
    window.addEventListener('messageDismissed', handleMessageDismissed);
    
    // Note: useWebsiteMessages hook handles periodic refresh globally (every 5 minutes)
    // No need to duplicate the interval here
    
    // Clean up subscriptions and event listeners
    return () => {
      unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('messageDismissed', handleMessageDismissed);
    };
  }, [user]); // Add user as dependency

  const loadMessages = async () => {
    if (!user) {
      setLoading(false);
      setIsVisible(false);
      return;
    }

    try {
      const activeMessages = await WebsiteMessageService.getActiveMessages();
      const dismissedIds = WebsiteMessageService.getDismissedMessageIds(user.id);
      
      // Filter out dismissed messages for this specific user AND user-specific messages
      // User-specific messages (task, team, survey, project) should only appear in the messages page, not in the top banner
      const visibleMessages = activeMessages.filter(msg => 
        !dismissedIds.includes(msg.id) && 
        !['task_pending_review', 'task_approved', 'task_rejected', 'team_application_received', 'team_application_approved', 'team_application_rejected', 'team_formed', 'survey_completed', 'project_release'].includes(msg.message_type)
      );
      
      // Only show non-dismissed, non-task messages
      setMessages(visibleMessages);
      setCurrentIndex(0);
      
      // If all messages are dismissed, hide the component
      if (visibleMessages.length === 0) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
    } catch (error) {
      console.error('Failed to load website messages:', error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = (messageId: string) => {
    if (!user) return;

    // Store dismissal in user-specific localStorage key
    localStorage.setItem(`dismissed_message_${user.id}_${messageId}`, new Date().toISOString());
    
    // Invalidate the message cache for this user so fresh data is loaded
    MessageCacheService.invalidateUserCache(user.id);
    
    // Remove the message from the current list
    const updatedMessages = messages.filter(msg => msg.id !== messageId);
    setMessages(updatedMessages);
    
    // Reset index if needed
    if (currentIndex >= updatedMessages.length) {
      setCurrentIndex(Math.max(0, updatedMessages.length - 1));
    }
    
    // Hide the component if no messages left
    if (updatedMessages.length === 0) {
      setIsVisible(false);
    }

    // Dispatch custom event to notify other components (like messages page)
    window.dispatchEvent(new CustomEvent('messageDismissed', {
      detail: { messageIds: [messageId], userId: user.id }
    }));
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % messages.length);
    setExpanded(false);
  };

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + messages.length) % messages.length);
    setExpanded(false);
  };

  const handleShowAllMessages = () => {
    navigate('/messages');
  };

  const handleDismissAll = () => {
    if (!user) return;

    // Store all message IDs as dismissed for this specific user
    const messageIds = messages.map(msg => msg.id);
    messages.forEach(msg => {
      localStorage.setItem(`dismissed_message_${user.id}_${msg.id}`, new Date().toISOString());
    });
    
    // Hide the component
    setIsVisible(false);

    // Dispatch custom event to notify other components (like messages page)
    window.dispatchEvent(new CustomEvent('messageDismissed', {
      detail: { messageIds, userId: user.id }
    }));
  };

  if (loading || messages.length === 0 || !isVisible) {
    return null;
  }

  // Get current message
  const currentMessage = messages[currentIndex];
  
  return (
    <div className="website-messages-container">
      {showAllMessages ? (
        // Show all messages view
        <div className="all-messages-view">
          <div className="all-messages-header">
            <h3>All Messages ({messages.length})</h3>
            <button 
              className="close-all-button"
              onClick={handleShowAllMessages}
              title="Go to messages page"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
          
          <div className="all-messages-list">
            {messages.map((msg, index) => (
              <div 
                key={msg.id}
                className="message-item"
                style={{
                  backgroundColor: msg.background_color,
                  color: msg.text_color,
                  borderColor: msg.text_color
                }}
                data-type={msg.message_type}
              >
                <div className="message-item-content">
                  <div className="message-icon">
                    {msg.message_type === 'welcome' && <i className="bi bi-hand-thumbs-up"></i>}
                    {msg.message_type === 'announcement' && <i className="bi bi-megaphone"></i>}
                    {msg.message_type === 'maintenance' && <i className="bi bi-tools"></i>}
                    {msg.message_type === 'info' && <i className="bi bi-info-circle"></i>}
                    {!isValidMessageType(msg.message_type) && <i className="bi bi-chat-dots"></i>}
                  </div>
                  <div className="message-text">{msg.message_text}</div>
                </div>
                <button 
                  className="message-dismiss"
                  onClick={() => handleDismiss(msg.id)}
                  title="Dismiss message"
                >
                  <i className="bi bi-x"></i>
                </button>
              </div>
            ))}
          </div>
          
          <div className="all-messages-footer">
            <button 
              className="dismiss-all-button"
              onClick={handleDismissAll}
              title="Dismiss all messages"
            >
              Dismiss All
            </button>
          </div>
        </div>
      ) : (
        // Carousel view (single message at a time)
        <div 
          className={`website-message ${expanded ? 'expanded' : ''}`}
          style={{
            backgroundColor: currentMessage.background_color,
            color: currentMessage.text_color,
            borderColor: currentMessage.text_color
          }}
          data-type={currentMessage.message_type}
        >
          <div className="message-content">
            <div className="message-icon">
              {currentMessage.message_type === 'welcome' && <i className="bi bi-hand-thumbs-up"></i>}
              {currentMessage.message_type === 'announcement' && <i className="bi bi-megaphone"></i>}
              {currentMessage.message_type === 'maintenance' && <i className="bi bi-tools"></i>}
              {currentMessage.message_type === 'info' && <i className="bi bi-info-circle"></i>}
              {!isValidMessageType(currentMessage.message_type) && (
                <i className="bi bi-chat-dots"></i>
              )}
            </div>

            <div className={`message-text ${expanded ? 'expanded' : ''}`} 
              style={{ color: "white", overflow: "hidden", textOverflow: "ellipsis", paddingTop: "2px" }}>
              {currentMessage.message_text}
            </div>
          </div>
          
          <div className="message-actions">
            {messages.length > 1 && (
              <>
                <button 
                  className="nav-button"
                  onClick={handlePrevious}
                  title="Previous message"
                  style={{ color: currentMessage.text_color }}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                
                <span className="message-counter">
                  {currentIndex + 1}/{messages.length}
                </span>
                
                <button 
                  className="nav-button"
                  onClick={handleNext}
                  title="Next message"
                  style={{ color: currentMessage.text_color }}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
                
                <button 
                  className="show-all-button"
                  onClick={handleShowAllMessages}
                  title="View all messages"
                  style={{ color: currentMessage.text_color }}
                >
                  <i className="bi bi-grid"></i>
                </button>
              </>
            )}
            
            <button 
              className="message-dismiss"
              onClick={() => handleDismiss(currentMessage.id)}
              title="Dismiss message"
              style={{ color: currentMessage.text_color }}
            >
              <i className="bi bi-x"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteMessageComponent;
